/**
 * Logger Setup and Initialization
 *
 * Provides a convenient way to initialize the logger based on environment.
 */

import { createLoggerConfig, initializeLogger, Logger } from './Logger'
import { createFirebaseRemoteLogger } from './FirebaseLoggerAdapter'

/**
 * Detect current environment from various sources
 */
function detectEnvironment(): 'development' | 'staging' | 'production' {
    // Check NODE_ENV
    if (typeof process !== 'undefined' && process.env) {
        const nodeEnv = process.env.NODE_ENV
        if (nodeEnv === 'production') return 'production'
        if (nodeEnv === 'staging') return 'staging'
        if (nodeEnv === 'development') return 'development'
    }

    // Check hostname for staging/production
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname
        if (hostname.includes('staging')) return 'staging'
        if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
            return 'development'
        }
        // Assume production for other domains
        return 'production'
    }

    // Default to development
    return 'development'
}

/**
 * Setup and initialize the logger with appropriate configuration
 */
export function setupLogger(
    environment?: 'development' | 'staging' | 'production'
): Logger {
    const env = environment || detectEnvironment()

    // Create Firebase remote logger
    const remoteLogger = createFirebaseRemoteLogger()

    // Create configuration based on environment
    const config = createLoggerConfig(env, remoteLogger)

    // Initialize and return logger
    const logger = initializeLogger(config)

    // Log initialization
    logger.info('Logger initialized', {
        metadata: {
            environment: env,
            minLevel: config.minLevel,
            consoleEnabled: config.enableConsole,
            remoteEnabled: config.enableRemote,
        },
    })

    return logger
}
